import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { xor } from 'lodash';
import {
  RecommendedPlanFoodSection,
  RecommendedExtraSection,
} from '@web/molecules';
import { ButtonLink } from '@web/atoms';
import {
  IngredientModal,
  useIngredientModal,
  ProductSelector,
} from '@web/components';
import { CheckoutBar } from '../CheckoutBar';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import { STATE_TO_STEP_REFERENCE_RECOMMENDED } from '../OnboardingFlowContainer/constants';
import { Header } from '../PlanSelectionContainer/Header';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import {
  userViewRecommendedPlanPage,
  userContinuesFromRecommendedPlan,
  userClickCreateOwnPlan,
  recipeSelected,
  recipeUnselected,
  userExpandsFreshRecipeDetails,
} from './events';
import { CHECKOUT_STEPS } from './constants';
import {
  StyledRecommendedPlanContainer,
  StyledRecommendedPlanCard,
  StyledForm,
  Headline,
  Subheadline,
  BodyText,
  LinkWrapper,
  Wrapper,
  HeaderContainer,
  FullHeadline,
  FullSubheadline,
  ProductCardContainer,
} from './styles';

export const RecommendedPlanForm = ({
  body,
  ctaText,
  onBackButtonClick,
  defaultRecipes,
  defaultExtras,
  extrasInfo,
  foodSectionProps,
  getConfigFromRecipes,
  onSubmit,
  onPersonalizePlanClick,
  period,
  personalizePlanText,
  pupName,
  recommendedDentalSku,
  savePercentText,
  unavailableExtras,
}) => {
  const [selectedRecipes] = useState(defaultRecipes);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState(defaultExtras);
  const reporter = useReporter();
  const {
    customer,
    isLoading: isCustomerLoading,
    isAppSession,
  } = useSessionCustomer();

  const { price, discountedPrice, message } =
    getConfigFromRecipes(selectedRecipes);

  const isValid =
    (selectedRecipes && selectedRecipes.fresh.length > 0) ||
    selectedRecipes.dry.length > 0;

  useEffect(() => {
    if (!isCustomerLoading) {
      reporter.tag(userViewRecommendedPlanPage(customer.email));
    }
  }, [reporter, isCustomerLoading, customer]);

  const onAddExtra = extra => {
    setSelectedExtras([...selectedExtras, extra]);
  };

  const onRemoveExtra = extra => {
    setSelectedExtras(selectedExtras.filter(e => e.sku !== extra));
  };

  const handleChange = extra => {
    const currExtra = selectedExtras.findIndex(e => e.sku === extra.sku);
    selectedExtras[currExtra].frequency = extra.frequency;
    selectedExtras[currExtra].quantity = extra.quantity;
  };

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    reporter.tag(userContinuesFromRecommendedPlan(customer.email));
    setIsSubmitting(true);
    await onSubmit(selectedRecipes, selectedExtras);
  };

  const onPersonalizePlanClickHandler = () => {
    reporter.tag(userClickCreateOwnPlan(customer.email));
    onPersonalizePlanClick();
  };

  const checkoutBarProps = {
    headline: pupName + "'s Recommended Plan",
    ctaText,
    isSubmitDisabled: isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    onBackButtonClick: onBackButtonClick,
    price,
    discountedPrice,
    message,
    period,
    savePercentText,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
  };

  const extraSectionProps = {
    allExtras: extrasInfo,
    unavailableExtras: unavailableExtras,
    selectedExtras: selectedExtras,
    recommendedDentalSku: recommendedDentalSku,
    removeExtra: onRemoveExtra,
    onAddExtrasClick: onAddExtra,
    onExtrasSelectionChange: handleChange,
  };
  return (
    <StyledForm onSubmit={onSubmitHandler}>
      <StyledRecommendedPlanContainer>
        <StyledRecommendedPlanCard>
          <Headline>{pupName.toUpperCase() + "'S"}</Headline>
          <Subheadline>Recommended Plan</Subheadline>
          <BodyText>{body + ' ' + pupName + '.'}</BodyText>
          <LinkWrapper>
            <ButtonLink onClick={onPersonalizePlanClickHandler}>
              {personalizePlanText}
            </ButtonLink>
          </LinkWrapper>
          <RecommendedPlanFoodSection {...foodSectionProps} />
          <RecommendedExtraSection {...extraSectionProps} />
        </StyledRecommendedPlanCard>
      </StyledRecommendedPlanContainer>
      <CheckoutBar {...checkoutBarProps} />
    </StyledForm>
  );
};
RecommendedPlanForm.displayName = 'RecommendedPlanForm';
RecommendedPlanForm.propTypes = {
  pupName: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  ctaText: PropTypes.string.isRequired,
  defaultRecipes: PropTypes.shape({
    fresh: PropTypes.array,
    dry: PropTypes.array,
  }),
  onBackButtonClick: PropTypes.func.isRequired,
  getConfigFromRecipes: PropTypes.func.isRequired,
  period: PropTypes.string.isRequired,
  savePercentText: PropTypes.string,
  onPersonalizePlanClick: PropTypes.func.isRequired,
  personalizePlanText: PropTypes.string.isRequired,
  extrasInfo: PropTypes.array.isRequired,
  defaultExtras: PropTypes.array.isRequired,
  unavailableExtras: PropTypes.array,
  recommendedDentalSku: PropTypes.string,
  productHeadline: PropTypes.string,
  calories: PropTypes.string.isRequired,
};

export const RecommendedFreshPlanForm = ({
  onSubmit,
  headline,
  subheadline,
  modals,
  ctaText,
  items: recipes,
  defaultRecipes,
  onBackButtonClick,
  unavailableRecipes,
  period,
  onPersonalizePlanClick,
  personalizePlanText,
  checkoutBarHeadline,
}) => {
  const [selectedRecipes, setSelectedRecipes] = useState(defaultRecipes);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reporter = useReporter();

  const {
    customer,
    isLoading: isCustomerLoading,
    isAppSession,
  } = useSessionCustomer();

  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);

  useEffect(() => {
    if (!isCustomerLoading) {
      reporter.tag(userViewRecommendedPlanPage(customer.email));
    }
  }, [reporter, isCustomerLoading, customer]);

  const onChange = recipes => {
    const currentRecipes = selectedRecipes ?? [];
    const recipeAdded = recipes.length > currentRecipes.length;
    const recipe = xor(recipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedRecipes(recipes);
  };

  const isValid = selectedRecipes && selectedRecipes.length > 0;
  const noSelectedRecipes = !selectedRecipes?.length;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    reporter.tag(userContinuesFromRecommendedPlan(customer.email));
    setIsSubmitting(true);
    await onSubmit(selectedRecipes);
  };

  const sortedSelectedRecipes = noSelectedRecipes ? [] : selectedRecipes;

  const checkoutBarProps = {
    ctaText,
    isSubmitDisabled: isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    onBackButtonClick: onBackButtonClick,
    period,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
  };

  const legend = `Choose up to ${
    recipes.length >= 4 ? '4' : recipes.length
  } recipe${recipes.length > 1 ? 's' : ''}`;

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    onRecipeDetailsClick(product.value);
  };

  const onPersonalizePlanClickHandler = () => {
    reporter.tag(userClickCreateOwnPlan(customer.email));
    onPersonalizePlanClick();
  };

  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    products: recipes,
    selected: sortedSelectedRecipes,
    unavailableProducts: unavailableRecipes,
    period,
    buttonIcon: 'plus',
  };

  return (
    <StyledForm onSubmit={onSubmitHandler}>
      <StyledRecommendedPlanContainer>
        <>
          <HeaderContainer>
            <FullHeadline>{headline}</FullHeadline>
            <FullSubheadline>{subheadline}</FullSubheadline>
            <LinkWrapper>
              <ButtonLink onClick={onPersonalizePlanClickHandler}>
                {personalizePlanText}
              </ButtonLink>
            </LinkWrapper>
          </HeaderContainer>
          <ProductCardContainer>
            <fieldset>
              {legend && <legend>{legend}</legend>}
              {
                <ProductSelector
                  {...productSelectorProps}
                  onChange={onChange}
                />
              }
            </fieldset>
            <IngredientModal {...modal} />
          </ProductCardContainer>
        </>
      </StyledRecommendedPlanContainer>
      <CheckoutBar {...checkoutBarProps} />
    </StyledForm>
  );
};
RecommendedFreshPlanForm.displayName = 'RecommendedFreshPlanForm';
RecommendedFreshPlanForm.propTypes = {
  subheadline: PropTypes.string,
  headline: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  ctaText: PropTypes.string.isRequired,
  defaultRecipes: PropTypes.array,
  onBackButtonClick: PropTypes.func.isRequired,
  period: PropTypes.string.isRequired,
  onPersonalizePlanClick: PropTypes.func.isRequired,
  personalizePlanText: PropTypes.string.isRequired,
  checkoutBarHeadline: PropTypes.string,
  unavailableRecipes: PropTypes.array,
  modals: PropTypes.array,
  items: PropTypes.array,
};

export const RecommendedPlanContainer = ({ children }) => {
  const { stateKey } = useContext(OnboardingContext);

  return (
    <Wrapper>
      <Header
        checkoutSteps={CHECKOUT_STEPS}
        step={STATE_TO_STEP_REFERENCE_RECOMMENDED[stateKey] || 0}
      />
      {children}
    </Wrapper>
  );
};

RecommendedPlanContainer.displayName = 'RecommendedPlanContainer';

RecommendedPlanContainer.propTypes = {
  children: PropTypes.object.isRequired,
};
